'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@/i18n/client';
import type { LessonBlockDto, MatchingPairDto } from '@vuganawe/types';

/* ─────────────────────────────────────────────────────────────────────────
   Interactive lesson exercises rendered by the lesson player.
   All state is local to the learner's browser; quiz blocks (scored, saved)
   stay in LessonReader — these are practice checks.
   ───────────────────────────────────────────────────────────────────────── */

/** Split "She [goes] to work." into text parts and gap answers. */
function parseGapSentence(sentence: string): { parts: string[]; answers: string[] } {
  const parts: string[] = [];
  const answers: string[] = [];
  let rest = sentence;
  for (;;) {
    const open = rest.indexOf('[');
    const close = rest.indexOf(']', open + 1);
    if (open === -1 || close === -1) break;
    parts.push(rest.slice(0, open));
    answers.push(rest.slice(open + 1, close).trim());
    rest = rest.slice(close + 1);
  }
  parts.push(rest);
  return { parts, answers };
}

function normalizeAnswer(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

/* ── Fill in the blanks ──────────────────────────────────────────────────── */

export function GapFillBlock({ block }: { block: LessonBlockDto }) {
  const t = useT();
  const sentences = useMemo(
    () => (block.items ?? []).map(parseGapSentence).filter((s) => s.answers.length > 0),
    [block.items],
  );
  const [values, setValues] = useState<Record<string, string>>({});
  const [checked, setChecked] = useState(false);

  if (sentences.length === 0) return null;

  const total = sentences.reduce((sum, s) => sum + s.answers.length, 0);
  const correct = sentences.reduce(
    (sum, sentence, si) =>
      sum +
      sentence.answers.filter(
        (answer, ai) => normalizeAnswer(values[`${si}:${ai}`] ?? '') === normalizeAnswer(answer),
      ).length,
    0,
  );
  const allFilled = sentences.every((sentence, si) =>
    sentence.answers.every((_, ai) => (values[`${si}:${ai}`] ?? '').trim() !== ''),
  );

  return (
    <section className="vn-w3__block vn-ex">
      <h2>{block.caption || t('ex.gapTitle')}</h2>
      <p className="vn-ex__hint">{t('ex.gapHint')}</p>
      <ol className="vn-ex__list">
        {sentences.map((sentence, si) => (
          <li key={si} className="vn-ex__sentence">
            {sentence.parts.map((part, pi) => (
              <span key={pi}>
                {part}
                {pi < sentence.answers.length ? (
                  <span className="vn-ex__gapwrap">
                    <input
                      className={`vn-ex__gap${
                        checked
                          ? normalizeAnswer(values[`${si}:${pi}`] ?? '') ===
                            normalizeAnswer(sentence.answers[pi] ?? '')
                            ? ' is-correct'
                            : ' is-wrong'
                          : ''
                      }`}
                      size={Math.max((sentence.answers[pi] ?? '').length, 4)}
                      value={values[`${si}:${pi}`] ?? ''}
                      disabled={checked}
                      aria-label={`Blank ${pi + 1} in sentence ${si + 1}`}
                      onChange={(e) =>
                        setValues((current) => ({ ...current, [`${si}:${pi}`]: e.target.value }))
                      }
                    />
                    {checked &&
                    normalizeAnswer(values[`${si}:${pi}`] ?? '') !==
                      normalizeAnswer(sentence.answers[pi] ?? '') ? (
                      <span className="vn-ex__solution">{sentence.answers[pi]}</span>
                    ) : null}
                  </span>
                ) : null}
              </span>
            ))}
          </li>
        ))}
      </ol>
      <div className="vn-ex__foot">
        {!checked ? (
          <button
            type="button"
            className="vn-btn vn-btn--primary vn-btn--small"
            disabled={!allFilled}
            onClick={() => setChecked(true)}
          >
            {t('common.checkAnswers')}
          </button>
        ) : (
          <>
            <span className="vn-ex__score">
              {t('common.correct', { correct, total })}
            </span>
            <button
              type="button"
              className="vn-btn vn-btn--secondary vn-btn--small"
              onClick={() => {
                setValues({});
                setChecked(false);
              }}
            >
              {t('common.tryAgain')}
            </button>
          </>
        )}
      </div>
    </section>
  );
}

/* ── Matching pairs ──────────────────────────────────────────────────────── */

/** Deterministic shuffle so the right column is stable across re-renders. */
function shuffled<T>(items: T[], seed: string): T[] {
  const out = [...items];
  let hash = 0;
  for (const char of seed) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  for (let i = out.length - 1; i > 0; i--) {
    hash = (hash * 1103515245 + 12345) & 0x7fffffff;
    const j = hash % (i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export function MatchingBlock({ block }: { block: LessonBlockDto }) {
  const t = useT();
  const pairs = useMemo(
    () => (block.pairs ?? []).filter((pair): pair is MatchingPairDto => Boolean(pair.left && pair.right)),
    [block.pairs],
  );
  const options = useMemo(() => shuffled(pairs.map((pair) => pair.right), block.id), [pairs, block.id]);
  const [choices, setChoices] = useState<Record<number, string>>({});
  const [checked, setChecked] = useState(false);

  if (pairs.length < 2) return null;

  const correct = pairs.filter((pair, index) => choices[index] === pair.right).length;
  const allChosen = pairs.every((_, index) => choices[index]);

  return (
    <section className="vn-w3__block vn-ex">
      <h2>{block.caption || t('ex.matchTitle')}</h2>
      <p className="vn-ex__hint">{t('ex.matchHint')}</p>
      <div className="vn-ex__pairs">
        {pairs.map((pair, index) => {
          const tone = checked ? (choices[index] === pair.right ? ' is-correct' : ' is-wrong') : '';
          return (
            <div key={index} className={`vn-ex__pair${tone}`}>
              <span className="vn-ex__pair-left">{pair.left}</span>
              <select
                className="vn-select vn-ex__pair-select"
                value={choices[index] ?? ''}
                disabled={checked}
                aria-label={`Match for “${pair.left}”`}
                onChange={(e) => setChoices((current) => ({ ...current, [index]: e.target.value }))}
              >
                <option value="">{t('ex.choose')}</option>
                {options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {checked && choices[index] !== pair.right ? (
                <span className="vn-ex__solution">{pair.right}</span>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="vn-ex__foot">
        {!checked ? (
          <button
            type="button"
            className="vn-btn vn-btn--primary vn-btn--small"
            disabled={!allChosen}
            onClick={() => setChecked(true)}
          >
            {t('common.checkAnswers')}
          </button>
        ) : (
          <>
            <span className="vn-ex__score">
              {t('common.correct', { correct, total: pairs.length })}
            </span>
            <button
              type="button"
              className="vn-btn vn-btn--secondary vn-btn--small"
              onClick={() => {
                setChoices({});
                setChecked(false);
              }}
            >
              {t('common.tryAgain')}
            </button>
          </>
        )}
      </div>
    </section>
  );
}

/* ── Dictation (listen → type → word-accuracy check) ─────────────────────── */

function words(value: string): string[] {
  return value
    .toLowerCase()
    .replace(/[.,!?;:'"“”‘’()\-–—]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

/** Longest-common-subsequence match so insertions/omissions don't cascade. */
function matchedWords(expected: string[], attempt: string[]): boolean[] {
  const rows = expected.length;
  const cols = attempt.length;
  const table: number[][] = Array.from({ length: rows + 1 }, () => new Array<number>(cols + 1).fill(0));
  for (let i = rows - 1; i >= 0; i--) {
    for (let j = cols - 1; j >= 0; j--) {
      table[i]![j] =
        expected[i] === attempt[j]
          ? table[i + 1]![j + 1]! + 1
          : Math.max(table[i + 1]![j]!, table[i]![j + 1]!);
    }
  }
  const matched = new Array<boolean>(rows).fill(false);
  let i = 0;
  let j = 0;
  while (i < rows && j < cols) {
    if (expected[i] === attempt[j]) {
      matched[i] = true;
      i += 1;
      j += 1;
    } else if (table[i + 1]![j]! >= table[i]![j + 1]!) {
      i += 1;
    } else {
      j += 1;
    }
  }
  return matched;
}

export function DictationBlock({ block }: { block: LessonBlockDto }) {
  const t = useT();
  const [attempt, setAttempt] = useState('');
  const [checked, setChecked] = useState(false);

  const expectedWords = useMemo(() => words(block.text ?? ''), [block.text]);
  if (!block.url || expectedWords.length === 0) return null;

  const matched = checked ? matchedWords(expectedWords, words(attempt)) : [];
  const correct = matched.filter(Boolean).length;
  const displayWords = (block.text ?? '').split(/\s+/).filter(Boolean);

  return (
    <section className="vn-w3__block vn-ex">
      <h2>{block.caption || t('ex.dictTitle')}</h2>
      <p className="vn-ex__hint">{t('ex.dictHint')}</p>
      <div className="vn-ex__model">
        <audio src={block.url} controls preload="none" />
      </div>
      <textarea
        className="vn-textarea vn-ex__dictation"
        rows={3}
        placeholder={t('ex.dictPlaceholder')}
        value={attempt}
        disabled={checked}
        onChange={(event) => setAttempt(event.target.value)}
      />
      {checked ? (
        <p className="vn-ex__transcript">
          {displayWords.map((word, index) => (
            <span key={index} className={matched[index] === false ? 'is-missed' : undefined}>
              {word}{' '}
            </span>
          ))}
        </p>
      ) : null}
      <div className="vn-ex__foot">
        {!checked ? (
          <button
            type="button"
            className="vn-btn vn-btn--primary vn-btn--small"
            disabled={words(attempt).length === 0}
            onClick={() => setChecked(true)}
          >
            {t('ex.dictCheck')}
          </button>
        ) : (
          <>
            <span className="vn-ex__score">
              {t('ex.dictScore', { correct, total: expectedWords.length })}
            </span>
            <button
              type="button"
              className="vn-btn vn-btn--secondary vn-btn--small"
              onClick={() => {
                setAttempt('');
                setChecked(false);
              }}
            >
              {t('common.tryAgain')}
            </button>
          </>
        )}
      </div>
    </section>
  );
}

/* ── Speaking practice (record & compare, stays on-device) ───────────────── */

type RecorderState = 'idle' | 'denied' | 'recording' | 'recorded';

export function SpeakingBlock({ block }: { block: LessonBlockDto }) {
  const t = useT();
  const [state, setState] = useState<RecorderState>('idle');
  const [supported, setSupported] = useState(true);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  useEffect(() => {
    setSupported(
      typeof window !== 'undefined' &&
        typeof window.MediaRecorder !== 'undefined' &&
        Boolean(navigator.mediaDevices?.getUserMedia),
    );
  }, []);

  // Release object URLs and the microphone when leaving the lesson.
  useEffect(() => {
    return () => {
      if (recordingUrl) URL.revokeObjectURL(recordingUrl);
      recorderRef.current?.stream.getTracks().forEach((track) => track.stop());
    };
  }, [recordingUrl]);

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        setRecordingUrl((current) => {
          if (current) URL.revokeObjectURL(current);
          return URL.createObjectURL(blob);
        });
        stream.getTracks().forEach((track) => track.stop());
        setState('recorded');
      };
      recorderRef.current = recorder;
      recorder.start();
      setState('recording');
    } catch {
      setState('denied');
    }
  }

  function stopRecording() {
    recorderRef.current?.stop();
  }

  return (
    <section className="vn-w3__block vn-ex vn-ex--speaking">
      <h2>{block.caption || t('ex.speakTitle')}</h2>
      {block.text ? <p className="vn-ex__prompt">{block.text}</p> : null}
      {block.url ? (
        <div className="vn-ex__model">
          <span className="vn-ex__model-label">{t('ex.listenFirst')}</span>
          <audio src={block.url} controls preload="none" />
        </div>
      ) : null}

      {!supported ? (
        <p className="vn-ex__hint">{t('ex.unsupported')}</p>
      ) : state === 'denied' ? (
        <p className="vn-ex__hint">{t('ex.denied')}</p>
      ) : (
        <div className="vn-ex__recorder">
          {state === 'recording' ? (
            <button type="button" className="vn-btn vn-btn--primary vn-btn--small" onClick={stopRecording}>
              {t('ex.stop')}
            </button>
          ) : (
            <button
              type="button"
              className="vn-btn vn-btn--primary vn-btn--small"
              onClick={() => void startRecording()}
            >
              {state === 'recorded' ? t('ex.recordAgain') : t('ex.record')}
            </button>
          )}
          {state === 'recording' ? <span className="vn-ex__reclive">{t('ex.recording')}</span> : null}
          {state === 'recorded' && recordingUrl ? (
            <div className="vn-ex__playback">
              <span className="vn-ex__model-label">{t('ex.yourRecording')}</span>
              <audio src={recordingUrl} controls />
            </div>
          ) : null}
        </div>
      )}
      <p className="vn-ex__privacy">{t('ex.privacy')}</p>
    </section>
  );
}
